import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './SafetyAuditLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  facilityType: string
}

function SafetyAuditLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          facilityType: data.facilityType,
          _subject: 'Lab Safety Audit Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Laboratory Safety Audit Services',
    description:
      'Professional laboratory safety audits including fume hood performance testing and ventilation compliance verification against NFPA 45 and ASHRAE 110 standards.',
    provider: { '@type': 'Organization', name: 'Laboratory Specialists' },
    serviceType: 'Laboratory Safety Compliance Audit',
  }

  return (
    <div className="audit-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero */}
      <section className="audit-hero">
        <div className="audit-hero-bg" aria-hidden />
        <div className="container audit-hero-inner">
          <p className="audit-hero-eyebrow">NFPA 45 Compliance · Fume Hood Testing · Ventilation Audits</p>
          <h1 className="audit-hero-title">
            Is Your Lab Actually Safe? Find Out Before an Inspector Does.
          </h1>
          <p className="audit-hero-subtitle">
            We audit laboratory ventilation systems and fume hoods for compliance with NFPA 45 and industry safety standards—identifying hazards, documenting deficiencies, and giving you a clear remediation plan.
          </p>
          <div className="audit-hero-cta">
            <button type="button" className="audit-cta-button" onClick={scrollToForm}>
              Schedule Your Free Consultation
            </button>
            <p className="audit-hero-cta-note">Response within 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Risk / urgency */}
      <section className="audit-risk">
        <div className="container">
          <h2 className="audit-section-title">The Risks of an Unaudited Lab</h2>
          <p className="audit-section-intro">
            Fume hoods and ventilation systems degrade over time. A hood that passed inspection five years ago may not protect your people today.
          </p>
          <div className="audit-risk-grid">
            <div className="audit-risk-card">
              <div className="audit-risk-icon" aria-hidden>⚠️</div>
              <h3>Chemical Exposure Liability</h3>
              <p>
                A fume hood operating below rated face velocity exposes researchers to hazardous vapors. NFPA 45 sets minimum performance standards specifically to prevent this—and liability falls on the facility if those standards aren't met.
              </p>
            </div>
            <div className="audit-risk-card">
              <div className="audit-risk-icon" aria-hidden>🔍</div>
              <h3>Failed Inspections & Fines</h3>
              <p>
                Regulatory inspections from OSHA, state fire marshals, and institutional EHS departments can result in citations, lab shutdowns, and costly emergency remediation when ventilation deficiencies are found without warning.
              </p>
            </div>
            <div className="audit-risk-card">
              <div className="audit-risk-icon" aria-hidden>🕰️</div>
              <h3>Aging Equipment</h3>
              <p>
                Fume hoods built before 1985 may be operational but fail modern ASHRAE 110 containment benchmarks. Belts, filters, and baffles degrade silently. A systematic audit identifies what needs replacement before it becomes a crisis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What we audit */}
      <section className="audit-scope">
        <div className="container">
          <h2 className="audit-section-title">What Our Safety Audit Covers</h2>

          <div className="audit-scope-grid">
            <div className="audit-scope-block">
              <h3>Fume Hood Performance Testing</h3>
              <ul>
                <li>Face velocity measurement and uniformity testing per ASHRAE 110</li>
                <li>Sash operation, containment, and airflow visualization</li>
                <li>Assessment of ducted and ductless hood performance</li>
                <li>Identification of corroded components, fogged windows, and degraded seals</li>
                <li>Fan belt condition and filter status (ductless systems)</li>
              </ul>
            </div>

            <div className="audit-scope-block">
              <h3>Ventilation System Compliance — NFPA 45</h3>
              <ul>
                <li>General laboratory ventilation rate verification (NFPA 45 Chapter 6)</li>
                <li>Exhaust system inspection for adequate air changes per hour</li>
                <li>Supply/exhaust balance evaluation to prevent pressure differentials</li>
                <li>Emergency ventilation system functionality check</li>
                <li>Documentation of deficiencies against NFPA 45 requirements</li>
              </ul>
            </div>

            <div className="audit-scope-block">
              <h3>Audit Report & Remediation Plan</h3>
              <ul>
                <li>Written report documenting all findings with pass/fail status</li>
                <li>Prioritized remediation recommendations by risk level</li>
                <li>Cost estimates for required repairs or replacements</li>
                <li>Compliance roadmap to meet NFPA 45 and institutional requirements</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NFPA 45 callout */}
      <section className="audit-nfpa">
        <div className="container">
          <div className="audit-nfpa-card">
            <h2>What Is NFPA 45?</h2>
            <p>
              NFPA 45, <em>Standard on Fire Protection for Laboratories Using Chemicals</em>, is the primary fire and safety code governing laboratory ventilation in the United States. It establishes requirements for:
            </p>
            <ul>
              <li>Minimum air change rates to control flammable vapor accumulation</li>
              <li>Fume hood exhaust design and performance</li>
              <li>Emergency ventilation provisions</li>
              <li>Separation of laboratory exhaust from HVAC return systems</li>
            </ul>
            <p>
              Compliance is required by most institutional EHS programs, and is referenced by OSHA and state fire codes. Non-compliance can result in immediate lab closure orders.
            </p>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="audit-audience">
        <div className="container">
          <h2 className="audit-section-title">Who Needs a Lab Safety Audit</h2>
          <ul className="audit-audience-list">
            <li>University and college chemistry, biology, and STEM labs due for a compliance review</li>
            <li>K–12 schools preparing for district or state safety inspections</li>
            <li>Pharmaceutical and biotech facilities undergoing regulatory audits</li>
            <li>Labs that have added new equipment or chemicals since their last inspection</li>
            <li>Facilities with fume hoods older than 10 years that have never been performance-tested</li>
            <li>Any lab whose last ventilation inspection was more than 2 years ago</li>
          </ul>
        </div>
      </section>

      {/* Objections */}
      <section className="audit-objections">
        <div className="container">
          <ul className="audit-bullets">
            <li>
              <strong>Already passed your last inspection?</strong> Inspections check for visible deficiencies—performance testing measures actual containment. Many hoods that pass visual inspection fail airflow testing.
            </li>
            <li>
              <strong>Not sure if you need a full audit?</strong> Start with a free consultation. We'll tell you honestly what level of review your facility needs based on age, equipment, and last inspection date.
            </li>
            <li>
              <strong>Concerned about cost?</strong> An audit costs a fraction of an emergency shutdown, a regulatory fine, or a workers' compensation claim from chemical exposure.
            </li>
          </ul>
        </div>
      </section>

      {/* Form */}
      <section className="audit-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="audit-form-card">
            <h2 className="audit-form-title">Schedule Your Free Consultation</h2>
            <p className="audit-form-lead-magnet">
              Tell us about your facility and we'll follow up within 24 hours to discuss your audit needs—no obligation.
            </p>

            {isSubmitted ? (
              <div className="audit-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll reach out to discuss your facility and schedule your safety consultation.</p>
              </div>
            ) : (
              <form className="audit-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="audit-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}

                <div className="audit-form-group">
                  <label htmlFor="audit-name">
                    Full Name <span className="audit-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="audit-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="audit-error" role="alert">{errors.name.message}</span>
                  )}
                </div>

                <div className="audit-form-group">
                  <label htmlFor="audit-email">
                    Email <span className="audit-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="audit-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="audit-error" role="alert">{errors.email.message}</span>
                  )}
                </div>

                <div className="audit-form-group">
                  <label htmlFor="audit-phone">
                    Phone <span className="audit-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="audit-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="audit-error" role="alert">{errors.phone.message}</span>
                  )}
                </div>

                <div className="audit-form-group">
                  <label htmlFor="audit-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="audit-company"
                    {...register('company')}
                    placeholder="Your lab or organization"
                  />
                </div>

                <div className="audit-form-group">
                  <label htmlFor="audit-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="audit-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="audit-form-group">
                  <label htmlFor="audit-facilityType">
                    Facility type <span className="audit-required">*</span>
                  </label>
                  <select
                    id="audit-facilityType"
                    {...register('facilityType', { required: 'Please select an option' })}
                    aria-invalid={errors.facilityType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="university">University / college laboratory</option>
                    <option value="k12">K–12 school science lab</option>
                    <option value="pharma-biotech">Pharmaceutical / biotech facility</option>
                    <option value="industrial">Industrial / quality control lab</option>
                    <option value="medical">Medical / clinical laboratory</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.facilityType && (
                    <span className="audit-error" role="alert">{errors.facilityType.message}</span>
                  )}
                </div>

                <p className="audit-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="audit-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Schedule My Free Consultation'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="audit-legal">
        <div className="container">
          <p className="audit-legal-text">
            Laboratory Specialists. Laboratory safety audit and ventilation compliance services. NFPA 45 references are for informational purposes; always consult your AHJ for jurisdiction-specific requirements.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default SafetyAuditLandingPage
